import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { Wallet, Phone, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { StatusBadge } from '../../components/StatusBadge';
import { KpiGrid, SectionHeader, ExportButtons, ReportTable, fIDR, fIDRCompact, fDateID } from './shared';

const CHART_AXIS = '#94A3B8';
const CHART_GRID = 'rgba(148,163,184,0.14)';
const TT_STYLE = { background: '#0B1020', border: '1px solid #334155', borderRadius: 8, padding: '8px 10px', fontSize: 12, color: '#EAF0FF' };
const TT_LABEL = { color: '#CBD5E1', fontWeight: 600, fontSize: 11 };
const TT_ITEM = { color: '#EAF0FF', fontSize: 12 };

export function ReceivablesTab({ data, loading, filters }) {
  const navigate = useNavigate();
  const s = data?.summary || {};
  const buckets = s.buckets || [];
  const cards = [
    { label: 'Total Outstanding', value: fIDRCompact(s.total_outstanding), accent: 'warn' },
    ...buckets.map(b => ({ label: b.label, value: fIDRCompact(b.amount), sub: `${b.count} inv`, accent: b.bucket === 'Current' ? 'success' : (b.bucket === '60+ Days' ? 'danger' : 'warn') })),
    { label: 'Client dengan Piutang', value: s.clients_with_receivable || 0, accent: '' },
  ];
  const chartData = buckets.map(b => ({ name: b.label, value: b.amount }));

  return (
    <div className="space-y-4">
      <SectionHeader icon={Wallet} title="Laporan Piutang / Aging" action={<ExportButtons tab="receivables" filters={filters} />} />
      <KpiGrid items={cards} loading={loading} columns={4} />

      <Card className="bg-card border-border">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-100">Aging Piutang per Bucket</CardTitle></CardHeader>
        <CardContent>
          {loading ? <Skeleton className="h-[200px] w-full" /> : (
            <div className="h-[220px] w-full">
              <ResponsiveContainer>
                <BarChart data={chartData} margin={{ left: 0, right: 6, top: 6, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: CHART_AXIS }} />
                  <YAxis tick={{ fontSize: 10, fill: CHART_AXIS }} tickFormatter={(v) => fIDRCompact(v).replace('Rp ', '')} />
                  <Tooltip contentStyle={TT_STYLE} labelStyle={TT_LABEL} itemStyle={TT_ITEM} formatter={(v) => [fIDR(v), 'Outstanding']} cursor={{ fill: 'rgba(148,163,184,0.08)' }} />
                  <Bar dataKey="value" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-100">Aging by Client</CardTitle></CardHeader>
        <CardContent className="p-0 sm:p-4">
          <ReportTable
            getRowKey={(r) => r.client_id}
            columns={[
              { key: 'client_name', label: 'Client', render: r => <span className="font-medium text-slate-100">{r.client_name || '—'}</span> },
              ...buckets.map(b => ({
                key: `b-${b.bucket}`, label: b.label, align: 'right',
                render: r => <span className="font-mono-num text-slate-200">{fIDR(r.buckets?.[b.bucket] || 0)}</span>,
              })),
              { key: 'total_outstanding', label: 'Total', align: 'right', render: r => <span className="font-mono-num text-amber-200 font-semibold">{fIDR(r.total_outstanding)}</span> },
            ]}
            rows={data?.rows_by_client || []}
            emptyText="Tidak ada piutang aktif pada periode ini."
            mobileCard={(r) => (
              <div className="text-sm space-y-1">
                <div className="font-semibold text-slate-100">{r.client_name}</div>
                <div className="grid grid-cols-2 gap-1 text-xs text-slate-300">
                  {buckets.map(b => (
                    <div key={b.bucket}>{b.label}: <span className="font-mono-num text-slate-100">{fIDR(r.buckets?.[b.bucket] || 0)}</span></div>
                  ))}
                </div>
                <div className="font-mono-num font-semibold text-amber-200 pt-1 border-t border-border/40 mt-1">Total: {fIDR(r.total_outstanding)}</div>
              </div>
            )}
          />
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-100">Aging by Invoice ({(data?.rows_by_invoice || []).length})</CardTitle></CardHeader>
        <CardContent className="p-0 sm:p-4">
          <ReportTable
            getRowKey={(r) => r.id}
            columns={[
              { key: 'client_name', label: 'Client', render: r => <span className="font-medium text-slate-100">{r.client_name}</span> },
              { key: 'invoice_number', label: 'No Invoice', render: r => <span className="font-mono-num text-slate-200">{r.invoice_number}</span> },
              { key: 'invoice_date', label: 'Tgl Inv', render: r => <span className="text-slate-300">{fDateID(r.invoice_date)}</span> },
              { key: 'event_date', label: 'Tgl Event', render: r => <span className="text-slate-300">{fDateID(r.event_date)}</span> },
              { key: 'venue', label: 'Venue', render: r => <span className="text-slate-400" title={r.venue}>{r.venue || '—'}</span> },
              { key: 'due_date', label: 'Jatuh Tempo', render: r => <span className="text-slate-300">{fDateID(r.due_date)}</span> },
              { key: 'aging_days', label: 'Aging', align: 'right', render: r => <span className="text-rose-300 font-medium">{r.aging_days}d</span> },
              { key: 'outstanding_amount', label: 'Outstanding', align: 'right', render: r => <span className="font-mono-num text-amber-200">{fIDR(r.outstanding_amount)}</span> },
              { key: 'status', label: 'Status', render: r => <StatusBadge status={r.status} /> },
              { key: 'last_follow_up_date', label: 'Last FU', render: r => <span className="text-xs text-slate-400">{r.last_follow_up_date ? fDateID(r.last_follow_up_date) : 'Belum'}</span> },
              { key: 'action', label: 'Aksi', align: 'right', render: r => (
                <div className="inline-flex gap-1">
                  <Button size="sm" variant="secondary" className="h-7 px-2 text-xs" onClick={(e) => { e.stopPropagation(); navigate(`/followups?invoice=${r.invoice_id}`); }}><Phone className="h-3 w-3" /></Button>
                  <Button size="sm" variant="secondary" className="h-7 px-2 text-xs" onClick={(e) => { e.stopPropagation(); navigate(`/invoices/${r.invoice_id}`); }}><ExternalLink className="h-3 w-3" /></Button>
                </div>
              ) },
            ]}
            rows={data?.rows_by_invoice || []}
            emptyText="Tidak ada piutang aktif pada periode ini."
            mobileCard={(r) => (
              <div className="text-sm space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-100 truncate">{r.client_name}</div>
                    <div className="text-xs text-slate-300 font-mono-num">{r.invoice_number}</div>
                  </div>
                  <StatusBadge status={r.status} />
                </div>
                <div className="grid grid-cols-2 gap-1 text-xs text-slate-300 mt-2">
                  <div>Due: {fDateID(r.due_date)}</div>
                  <div>Aging: <span className="text-rose-300 font-medium">{r.aging_days}d</span></div>
                </div>
                <div className="font-mono-num font-semibold text-amber-200 mt-1">{fIDR(r.outstanding_amount)}</div>
              </div>
            )}
          />
        </CardContent>
      </Card>
    </div>
  );
}
